import { useSuspenseQuery } from '@tanstack/react-query'

import { centsToDollar } from '@/lib/currency'
import {
  orgFeesQueryOptions,
  orgInfoQueryOptions,
} from '@/queries/organization'
import { useOrganizationStore } from '@/store/organization/organization'
import { OrganizationInfoResponse, PayoutFeeType } from '@/types/organization'

export const selectOrgData = (data?: OrganizationInfoResponse | null) => {
  const orgData = data?.data[0]
  const orgName = orgData?.name
  const appName = orgData?.app_display_name ?? ''
  const directEwaEnabled = orgData?.ewa.direct_enabled ?? false
  const usesEarnings = orgData?.ewa.period_earnings ?? false
  const hoursOrEarnings = usesEarnings ? 'earnings' : 'hours'
  const withdrawalPercentage = orgData?.ewa?.withdrawal_percentage ?? 0
  const usesUnifiedOnboarding = orgData?.enable_unified_onboarding ?? false
  const onboardingRequireW9Collection =
    orgData?.tax_filing?.onboarding_require_w9_collection ?? false

  return {
    appName,
    orgName,
    directEwaEnabled,
    orgInfo: orgData,
    usesEarnings,
    hoursOrEarnings,
    withdrawalPercentage,
    usesUnifiedOnboarding,
    onboardingRequireW9Collection,
  }
}

export const useOrg = () => {
  const orgId = useOrganizationStore(state => state.orgId)
  return useSuspenseQuery({
    ...orgInfoQueryOptions(orgId),
    select: selectOrgData,
  })
}

export const useFormattedOrgFees = (
  payoutFeeType: PayoutFeeType = PayoutFeeType.INSTANT
) => {
  const orgId = useOrganizationStore(state => state.orgId)
  return useSuspenseQuery({
    ...orgFeesQueryOptions(orgId),
    select: data => {
      const feeType = payoutFeeType
      const instantFee = data?.find(fee => fee.payout_fee_type === feeType)
      if (!instantFee) return

      const { base_fee, fee_percent } = instantFee
      const orgCoversAllFees = base_fee === 0 && fee_percent === 0

      let baseFee = ''
      const feePercentage = fee_percent > 0 ? `${fee_percent}%` : ''

      if (base_fee > 0 && fee_percent > 0) {
        baseFee = `(${centsToDollar(base_fee)} minimum)`
      } else if (base_fee > 0) {
        baseFee = `${centsToDollar(base_fee)}`
      }

      return {
        baseFee,
        feePercentage,
        orgCoversAllFees,
      }
    },
  })
}
